import { Menu, MenuItem, useTheme } from '@mui/material'
import { MouseEvent, ReactElement, useState } from 'react'
import { IconButton } from 'ComponentLibrary/src/piazza'
import { DotsThreeVerticalIcon, IconContext, PlusCircleIcon } from '@phosphor-icons/react'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { InfoTooltip } from 'ComponentLibrary/src/atoms/IconTooltip'

export type MenuOption = {
    title: string
    callback: () => void
    danger?: boolean
    icon?: ReactElement
    ariaLabel?: string
    disabled?: boolean
    tooltip?: string
}
interface OptionsMenuProps {
    options: MenuOption[]
    className?: string
    disabled?: boolean
    ariaLabel?: string
    icon?: 'dots' | 'plus'
    variant?: 'primary' | 'secondary' | 'destructive' | 'tertiary'
    tooltip?: string
    CustomIcon?: ReactElement
    size?: 'small' | 'medium'
}

export const OptionsMenu = ({
    options,
    className,
    disabled = false,
    ariaLabel,
    icon = 'dots',
    variant = 'tertiary',
    size = 'small',
    tooltip,
    CustomIcon,
}: OptionsMenuProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const theme = useTheme()

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        event.preventDefault()
        setAnchorEl(event.currentTarget)
    }

    const handleClose = (event: MouseEvent<HTMLLIElement>) => {
        event.stopPropagation()
        event.preventDefault()
        setAnchorEl(null)
    }

    const getIcon = () => {
        if (CustomIcon) {
            return CustomIcon
        }
        if (icon === 'plus') {
            return <PlusCircleIcon />
        }
        return (
            <DotsThreeVerticalIcon
                style={{ scale: '1.1' /* This scale is needed on HD monitors so the dots do not look like mini lines */ }}
            />
        )
    }

    return (
        <div className={className}>
            <IconButton
                size={size}
                variant={variant}
                disabled={disabled}
                onClick={handleClick}
                aria-label={ariaLabel || 'options'}
                icon={getIcon()}
                tooltip={tooltip}
            />
            <Menu anchorEl={anchorEl} keepMounted open={!!anchorEl} onClose={handleClose}>
                {options.map((option) => (
                    <MenuItem
                        key={`menu-item-${option.title}`}
                        style={{ color: option.danger ? theme.palette.danger.main : undefined, gap: '10px' }}
                        onClick={(event) => {
                            option.callback()
                            handleClose(event)
                        }}
                        aria-label={option.ariaLabel}
                        disabled={option.disabled}
                    >
                        <IconContext.Provider
                            value={{
                                size: tokens.SizingIconS,
                                weight: 'bold',
                            }}
                        >
                            {option.icon}
                        </IconContext.Provider>
                        <span>{option.title}</span>
                        {option.tooltip && <InfoTooltip style={{ pointerEvents: 'auto' }} text={option.tooltip} />}
                    </MenuItem>
                ))}
            </Menu>
        </div>
    )
}

export default OptionsMenu
